import { Steps } from 'antd'
import {
  FC,
  JSXElementConstructor,
  ReactElement,
  cloneElement,
  useState,
} from 'react'
import { Form as AntdForm } from 'antd'
import {
  Button,
  Form,
  Heading,
  Left,
  NavigationModal,
  Right,
} from '@lmc/ui-kit'
import { styled } from 'styled-components'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import { useTranslation } from 'react-i18next'
import ModalLayout from './ModalLayout'

type StepForm = {
  title: string
  form: ReactElement<any, string | JSXElementConstructor<any>>
}

export type InitialState = Record<string, any>

type ModalStepperFormProps = {
  visible: boolean
  steps: StepForm[]
  title: string
  onCancel: () => void
  onFinish: () => Promise<boolean>
  isLoading?: boolean
  hasStepper?: boolean
  formData: InitialState
  setFormData: (values: InitialState) => void
}

export const ModalStepperForm: FC<ModalStepperFormProps> = ({
  visible,
  steps = [],
  title,
  onCancel,
  onFinish,
  isLoading,
  hasStepper = true,
  formData,
  setFormData,
}) => {
  const [form] = AntdForm.useForm()

  const { t } = useTranslation()

  const [step, setStep] = useState(0)

  const handleOnCancel = () => {
    handleConfirmCancel()
  }

  const handleBaseCancel = () => {
    onCancel()
    setStep(0)
    form.resetFields()
    setFormData({} as InitialState)
  }

  const handleConfirmCancel = () => {
    if (hasFormDataTouched) {
      setShowPrompt(true)
      return
    }
    handleBaseCancel()
  }

  const [hasFormDataTouched, setHasFormDataTouched] = useState(false)

  const [showPrompt, setShowPrompt] = useState(false)

  const handleFormValuesChange = (values: unknown) => {
    if (!hasFormDataTouched) {
      setHasFormDataTouched(true)
    }
    // @ts-ignore
    setFormData((prev) => ({ ...prev, ...values }))
  }

  const isFinalConfirm = step + 1 === steps?.length

  const handleOnUpdateFinish = async (values: InitialState) => {
    await form.validateFields()
    setFormData((prev: InitialState) => ({ ...prev, ...values }))
    if (isFinalConfirm) {
      if (hasFormDataTouched) {
        const res = await onFinish()
        if (res) {
          setShowPrompt(false)
          setHasFormDataTouched(false)
          handleBaseCancel()
        }
      }

      return
    }
    setStep((prev) => prev + 1)
  }

  return (
    <>
      <ModalLayout
        onCancel={handleOnCancel}
        open={visible}
        headerContent={
          <>
            <StyledHeading level={2}>{title}</StyledHeading>
            {hasStepper && (
              <StyledSteps
                current={step}
                items={steps?.map((step) => ({
                  key: step.title,
                  title: step.title,
                }))}
              />
            )}
            <StyledDivider />
          </>
        }
        footerContent={
          <>
            {step !== 0 && (
              <Button
                variant="text"
                onClick={() => setStep((prev) => prev - 1)}
                data-cy="btn-back-modal"
              >
                <Left /> {t('BACK')}
              </Button>
            )}
            <StyledNextButton
              variant="primary"
              onClick={form.submit}
              isLoading={isLoading}
              data-cy="btn-next-modal"
            >
              {t(isFinalConfirm ? 'SAVE' : 'NEXT')}
              <Right />
            </StyledNextButton>
          </>
        }
      >
        <Form
          form={form}
          onFinish={handleOnUpdateFinish}
          onValuesChange={handleFormValuesChange}
          initialValues={formData}
        >
          {steps?.map((stepForm, index) => {
            if (index === step) {
              return cloneElement(stepForm.form, {
                formData,
                form,
                setFormData,
              })
            }
            return null
          })}
        </Form>
      </ModalLayout>
      <NavigationModal
        showPrompt={showPrompt}
        modalTitle={t('ARE_YOU_SURE')}
        modalContentText={t('BY_EXITING')}
        modalConfirmText={t('EXIT')}
        modalCancelText={t('CANCEL')}
        confirmNavigation={() => {
          setShowPrompt(false)
          setHasFormDataTouched(false)
          handleBaseCancel()
        }}
        cancelNavigation={() => setShowPrompt(false)}
      />
    </>
  )
}

const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  font-size: 24px;
`

const StyledSteps = styled(Steps)`
  .ant-steps-item-icon {
    width: 24px;
    height: 24px;
    line-height: 24px;
    margin-top: 5px;
  }

  .ant-steps-item-process {
    .ant-steps-item-icon {
      .ant-steps-icon {
        color: ${(props) => props.theme.color.neutral.color11} !important;
      }
    }
  }
`

const StyledDivider = styled(Divider)`
  margin-top: 1vh;
`

const StyledNextButton = styled(Button)`
  margin-left: auto;
`
